/* ========================================
   FILTER UTILITIES
   Configuration, utility functions, and caching
   Extracted from collection-filter.js
   ======================================== */

(function() {
  'use strict';

  // ========================================
  // CONFIGURATION & CONSTANTS
  // ========================================
  
  const CONFIG = {
    localStorageKeys: {
      filterOpen: 'collection_filter_open',
      filterValue: 'filter_value',
      accordionStates: 'accordion_states',
      selectedFilters: 'selected_filters'
    },
    selectors: {
      showFilterBtn: '#show_filter',
      collectionFilter: '.collection-filter',
      closeFilterBtn: '.filter-close-button',
      blurCover: '.desktop-blur-cover',
      filterTags: '.collection-filter__tag a',
      filterTagItems: '.collection-filter__tag',
      sortSelect: '#sort-by',
      filterBarContainer: '.filter--bar__container',
      filterBarCarousel: '.filter-bar--carousel',
      filterCollectionBoxes: '.filter-collection-box',
      accordionDetails: '[data-accordion-details]',
      filterLoadingOverlay: '#filter-loading-overlay',
      collectionLoadingOverlay: '#collection-loading-overlay',
      productsWrapper: '.collection__products-wrapper',
      collectionMain: '.collection__main',
      selectedFiltersContainer: '#selected-filters-container',
      selectedFiltersWrapper: '#selected-filters-wrapper',
      selectedFiltersList: '#selected-filters-list',
      clearAllFiltersBtn: '#clear-all-filters',
      applyButton: '#filter-apply-button',
      applyButtonCount: '#filter-apply-button-count',
      applyContainer: '#filter-apply-container',
      applyButtonText: '.filter-apply-button-text',
      collectionElement: '[data-collection-handle], .collection',
      productCard: 'product-card, .product-card, [data-product-card], .card'
    },
    breakpoints: {
      desktop: 990,
      mobile: 768
    }
  };

  // ========================================
  // UTILITY FUNCTIONS
  // ========================================
  
  const Utils = {
    isDesktop: () => window.innerWidth >= CONFIG.breakpoints.desktop,
    isMobile: () => window.innerWidth <= CONFIG.breakpoints.mobile,
    
    getLocalStorage: (key) => localStorage.getItem(key),
    setLocalStorage: (key, value) => localStorage.setItem(key, value),
    removeLocalStorage: (key) => localStorage.removeItem(key),
    
    log: () => {},
    error: (message, data = null) => {
      console.error(`[CollectionFilter] ${message}`, data || '');
    },
    warn: () => {},
    
    showError: (message) => {
      // Show user-friendly error message
      const errorToast = document.createElement('div');
      errorToast.className = 'filter-error-toast';
      errorToast.style.cssText = 'position: fixed; top: 20px; right: 20px; background: #ff4444; color: white; padding: 16px 24px; border-radius: 8px; z-index: 1000000; box-shadow: 0 4px 12px rgba(0,0,0,0.15); max-width: 400px;';
      errorToast.textContent = message;
      document.body.appendChild(errorToast);
      
      // Use requestAnimationFrame for smooth fade-out animation
      requestAnimationFrame(() => {
        setTimeout(() => {
          errorToast.style.opacity = '0';
          errorToast.style.transition = 'opacity 0.3s';
          // Use requestAnimationFrame for removal after transition
          requestAnimationFrame(() => {
            setTimeout(() => errorToast.remove(), 300);
          });
        }, 5000);
      });
      
      Utils.error(message);
    },

    // Debounce function for performance optimization
    debounce: (func, delay) => {
      let timeoutId;
      return (...args) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => func.apply(this, args), delay);
      };
    },

    // Throttle function for scroll events
    throttle: (func, delay) => {
      let inThrottle;
      return (...args) => {
        if (!inThrottle) {
          func.apply(this, args);
          inThrottle = true;
          setTimeout(() => inThrottle = false, delay);
        }
      };
    },

    // Efficient DOM update using DocumentFragment
    updateDOMEfficiently: (targetElement, newHTML) => {
      if (!targetElement) return;
      
      const fragment = document.createDocumentFragment();
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = newHTML;
      
      while (tempDiv.firstChild) {
        fragment.appendChild(tempDiv.firstChild);
      }
      
      // Use batch update
      requestAnimationFrame(() => {
        targetElement.innerHTML = '';
        targetElement.appendChild(fragment);
      });
    },

    // Preload resources for better performance
    preloadResource: (url, type = 'fetch') => {
      if (type === 'fetch') {
        const link = document.createElement('link');
        link.rel = 'prefetch';
        link.href = url;
        document.head.appendChild(link);
      }
    }
  };

  // ========================================
  // URL UTILITIES (Using Native URLSearchParams)
  // ========================================
  
  const UrlUtils = {
    /**
     * Parse URL and return URL object with searchParams
     * @param {string|URL} url - URL string or URL object
     * @returns {URL} URL object with parsed searchParams
     */
    parseUrl(url) {
      try {
        if (url instanceof URL) {
          return new URL(url);
        }
        return new URL(url, window.location.origin);
      } catch (e) {
        // Fallback for relative URLs
        return new URL(url, window.location.href);
      }
    },

    /**
     * Get base URL without query parameters
     * @param {string|URL} url - URL string or URL object (defaults to current location)
     * @returns {string} Base URL without query string
     */
    getBaseUrl(url = window.location.href) {
      const urlObj = this.parseUrl(url);
      return urlObj.origin + urlObj.pathname;
    },

    /**
     * Get query parameters as URLSearchParams object
     * @param {string|URL} url - URL string or URL object (defaults to current location)
     * @returns {URLSearchParams} URLSearchParams object
     */
    getSearchParams(url = window.location.href) {
      const urlObj = this.parseUrl(url);
      return urlObj.searchParams;
    },

    /**
     * Get a specific query parameter value
     * @param {string} key - Parameter name
     * @param {string|URL} url - URL string or URL object (defaults to current location)
     * @returns {string|null} Parameter value or null
     */
    getParam(key, url = window.location.href) {
      const searchParams = this.getSearchParams(url);
      return searchParams.get(key);
    },

    /**
     * Set a query parameter value
     * @param {string} key - Parameter name
     * @param {string} value - Parameter value
     * @param {string|URL} url - URL string or URL object (defaults to current location)
     * @returns {string} Updated URL string
     */
    setParam(key, value, url = window.location.href) {
      const urlObj = this.parseUrl(url);
      urlObj.searchParams.set(key, value);
      return urlObj.pathname + urlObj.search;
    },

    /**
     * Delete a query parameter
     * @param {string} key - Parameter name
     * @param {string|URL} url - URL string or URL object (defaults to current location)
     * @returns {string} Updated URL string
     */
    deleteParam(key, url = window.location.href) {
      const urlObj = this.parseUrl(url);
      urlObj.searchParams.delete(key);
      return urlObj.pathname + urlObj.search;
    },

    /**
     * Build URL with multiple query parameters
     * @param {string|URL} baseUrl - Base URL
     * @param {Object} params - Object with key-value pairs for query parameters
     * @param {boolean} preserveExisting - Whether to preserve existing query params (default: true)
     * @returns {string} URL string with query parameters
     */
    buildUrl(baseUrl, params = {}, preserveExisting = true) {
      const urlObj = this.parseUrl(baseUrl);
      
      if (!preserveExisting) {
        urlObj.search = '';
      }
      
      // Use URLSearchParams to efficiently set multiple parameters
      Object.entries(params).forEach(([key, value]) => {
        if (value === null || value === undefined || value === '') {
          urlObj.searchParams.delete(key);
        } else {
          urlObj.searchParams.set(key, String(value));
        }
      });
      
      return urlObj.pathname + urlObj.search;
    },

    /**
     * Parse filter parameter from URL (e.g., "filter.v.availability=1")
     * Handles both query string format and path-based filters
     * @param {string|URL} url - URL string or URL object (defaults to current location)
     * @returns {Object} Object with filter parameters
     */
    parseFilterParams(url = window.location.href) {
      const urlObj = this.parseUrl(url);
      const searchParams = urlObj.searchParams;
      const filters = {};
      
      // Parse query string filters (e.g., ?filter.v.availability=1)
      for (const [key, value] of searchParams.entries()) {
        if (key.startsWith('filter.')) {
          filters[key] = value;
        }
      }
      
      return filters;
    },

    /**
     * Build URL with sort_by parameter using URLSearchParams
     * @param {string} sortValue - Sort value (e.g., "price-ascending" or "filter.v.availability=1")
     * @param {string|URL} baseUrl - Base URL (defaults to current location)
     * @returns {string} URL string with sort parameter
     */
    buildSortUrl(sortValue, baseUrl = window.location.href) {
      const urlObj = this.parseUrl(baseUrl);
      
      // Handle availability filter (special case)
      if (sortValue === 'filter.v.availability=1') {
        urlObj.searchParams.set('filter.v.availability', '1');
        // Remove sort_by if it exists
        urlObj.searchParams.delete('sort_by');
      } else {
        // Regular sort option
        urlObj.searchParams.set('sort_by', sortValue);
        // Remove availability filter if it exists
        urlObj.searchParams.delete('filter.v.availability');
      }
      
      return urlObj.pathname + urlObj.search;
    }
  };

  // ========================================
  // ADVANCED CACHING SYSTEM
  // ========================================
  
  const CacheManager = {
    cache: new Map(),
    cacheExpiry: 20 * 60 * 1000, // 20 minutes for collection/filter pages (balance of freshness and fewer requests)
    
    generateKey: (url, params = {}) => {
      const keyParams = new URLSearchParams(params).toString();
      return `${url}${keyParams ? '?' + keyParams : ''}`;
    },
    
    set: function(key, data) {
      const item = {
        data,
        timestamp: Date.now(),
        expires: Date.now() + CacheManager.cacheExpiry
      };
      CacheManager.cache.set(key, item);
    },
    
    get: function(key) {
      const item = CacheManager.cache.get(key);
      if (!item) return null;
      
      if (Date.now() > item.expires) {
        CacheManager.cache.delete(key);
        return null;
      }
      return item.data;
    },
    
    clear: function() {
      CacheManager.cache.clear();
    },

    // Clean expired entries
    cleanup: function() {
      const now = Date.now();
      for (const [key, item] of CacheManager.cache.entries()) {
        if (now > item.expires) {
          CacheManager.cache.delete(key);
        }
      }
    }
  };

  // ========================================
  // GLOBAL EXPORTS
  // ========================================
  
  // Expose utilities globally for other filter modules
  window.FilterUtils = { CONFIG, Utils, UrlUtils };
  window.CacheManager = CacheManager;

})();
